import { Button, Typography, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import { useFormikContext } from 'formik';
import { Box1 } from 'iconsax-react';
import { type FC, useContext, useState } from 'react';

import { SlabContext } from '@core/context/SlabContext';
import type { ProjectOrQuoteProductFormikType } from '@core/formik/projectOrQuoteProduct';
import type { ProductsByPlantFormikType, QuoteFormikType } from '@core/formik/quote';
import {
  NewEmptySharedFormikProduct,
  type PlantDistanceInfo,
  updateFormikProduct,
} from '@core/hooks/useProductSection';
import Enums from '@core/types/api/Enums';
import type { FixedPricingByCompany } from '@core/types/api/FixedPricingByCompany/FixedPricingByCompany';
import type { Product } from '@core/types/api/Product/Product';
import type { TenantDefaultMargins } from '@core/types/tenantDefaultMargins';
import { toRecordById } from '@core/utils/domainHelpers';
import { hasStringValue } from '@core/utils/equality';
import { logger } from '@core/utils/logger';
import { buildCountPhrase } from '@core/utils/strings';
import { randomUUID } from '@core/utils/uuid';
import InputSearch from '@web/components/InputSearch/InputSearch';
import type { ProductSectionProps } from '@web/components/ProductSection/ProductSection';
import { useSlabQuery } from '@web/hooks/useSlabQuery';
import { useTenantMargins } from '@web/hooks/useTenantMargins';
import { useToast } from '@web/providers/ToastProvider/useToast';

import { ProductLocationSelectList } from './ProductLocationSelectList';
import { type ProductPerPlant, useLocationProductsGrouping } from './useLocationProductsGrouping';

export type SelectedPlant = {
  id: string;
  name: string;
  distanceInfo: PlantDistanceInfo;
  copyProducts: Product[];
  notCopied: number;
};

type CopyLocationFormProps = {
  curProductsRef: ProductSectionProps['curProductsRef'];
  plantName: string;
  products: Omit<ProjectOrQuoteProductFormikType, 'displayOnly'>[];
  excludePlantIds: string[];
  sectionType: Enums.QuoteProductKind;
  plantDistanceInfos: Map<string, PlantDistanceInfo>;
  onClose?: () => void;
  onCopy?: (toInsert: ProductsByPlantFormikType[]) => void;
};

export const createFormikPlantsForProductsToInsert = (
  selectedPlants: SelectedPlant[],
  marginConfiguration: Enums.MarginConfiguration,
  defaultMargins: TenantDefaultMargins,
  sectionType: Enums.QuoteProductKind,
  isMoneySwitchOn: boolean,
  sourceProducts: Omit<ProjectOrQuoteProductFormikType, 'displayOnly'>[],
  defaultToSuggestedPrice: boolean,
  fixedPricingByCompany: FixedPricingByCompany | undefined,
): ProductsByPlantFormikType[] => {
  return selectedPlants.map((plant) => {
    return {
      clientId: randomUUID(),
      plant: { id: plant.id, option: { label: plant.name, value: plant.id } },
      plantDistance: plant.distanceInfo.plantDistance,
      plantDistanceUnit: plant.distanceInfo.plantDistanceUnit,
      plantDriveTime: plant.distanceInfo.plantDriveTime ?? null,
      products: plant.copyProducts.map((product) => {
        const formikProduct = {
          ...NewEmptySharedFormikProduct({
            sectionType,
          }),
          isMoneySwitchOn,
        };
        const sourceProduct = sourceProducts.find(
          (s) => s.product.option?.label === product.alternateID,
        );
        const quantity = sourceProduct?.quantity ?? null;
        return updateFormikProduct(formikProduct, {
          distanceInfo: plant.distanceInfo,
          newProjectProduct: undefined,
          newPlantProduct: product,
          marginConfiguration,
          defaultMargins,
          quantity,
          usage: formikProduct.usage,
          defaultToSuggestedPrice,
          fixedPricingByCompany,
          plantId: plant.id,
          isQuoteLine: true,
        });
      }),
    };
  });
};

const makeSelectedPlants = (
  plants: ProductPerPlant[],
  selectedPlantsIds: string[],
  plantDistanceInfos: Map<string, PlantDistanceInfo>,
): SelectedPlant[] => {
  return plants
    .filter((plant) => selectedPlantsIds.includes(plant.plantId))
    .map((plant): SelectedPlant => {
      return {
        id: plant.plantId,
        name: plant.plantName,
        distanceInfo: plantDistanceInfos.get(plant.plantId) || {
          plantDistance: null,
          plantDistanceUnit: null,
          plantDriveTime: null,
        },
        copyProducts: plant.match,
        notCopied: plant.notMatch.length,
      };
    });
};

export const CopyLocationForm: FC<CopyLocationFormProps> = ({
  curProductsRef,
  onClose,
  excludePlantIds,
  onCopy,
  plantDistanceInfos,
  sectionType,
  plantName,
  products,
}) => {
  const theme = useTheme();
  const toast = useToast();
  const productNames = products.map((product) => product.product.option?.label || '');

  const { plants, isLoading } = useLocationProductsGrouping(excludePlantIds, productNames);
  const [selectedPlantsIds, setSelectedPlantsIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const filteredPlants = plants.filter(({ plantName: targetPlantName }) => {
    const preparedSearch = search.trim().toLowerCase();
    if (!preparedSearch) {
      return true;
    }
    return targetPlantName.toLowerCase().indexOf(preparedSearch) !== -1;
  });
  const { defaultIsMoneySwitchOn, marginConfiguration, defaultMargins } = useTenantMargins();
  const { userInfo } = useContext(SlabContext);
  const defaultToSuggestedPrice = userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagDefaultPriceToSuggestedPrice,
  ]);
  const hasTierPricing =
    userInfo.hasFlags([Enums.FeatureFlagName.FeatureFlagTierPricing]) &&
    userInfo.tenant.tierPricing;

  const formikBag = useFormikContext<QuoteFormikType>();
  const companyId = formikBag.values.company?.id ?? null;
  const { data: fixedPricingByCompany } = useSlabQuery(
    'GET tier pricing by company ID',
    { queryParams: { companyIds: [companyId ?? ''] } },
    { enabled: hasTierPricing && hasStringValue(companyId) },
  );
  const onCopyClick = (): void => {
    onClose?.();
    try {
      const selectedPlants = makeSelectedPlants(plants, selectedPlantsIds, plantDistanceInfos);
      const totalCopied = selectedPlants.reduce((acc, val) => acc + val.copyProducts.length, 0);
      const totalNotCopied = selectedPlants.reduce((acc, val) => acc + val.notCopied, 0);

      onCopy?.(
        createFormikPlantsForProductsToInsert(
          selectedPlants,
          marginConfiguration,
          defaultMargins,
          sectionType,
          defaultIsMoneySwitchOn,
          products,
          defaultToSuggestedPrice,
          fixedPricingByCompany,
        ),
      );
      const newProducts = toRecordById(
        selectedPlants.flatMap((plant) => plant.copyProducts),
        (product) => product,
      );
      curProductsRef.current = {
        ...curProductsRef.current,
        ...newProducts,
      };

      const productsCountPhrase = buildCountPhrase(totalCopied, '', 'product', 'products');
      const locationsCountPhrase = buildCountPhrase(
        selectedPlants.length,
        'new',
        'location',
        'locations',
      );
      const productsNotCopiedCountPhrase = buildCountPhrase(
        totalNotCopied,
        'unavailable',
        'product',
        'products',
      );

      const message =
        totalNotCopied === 0
          ? `Products copied successfully - ${productsCountPhrase} copied to ${locationsCountPhrase}`
          : `Partial copy completed - ${totalCopied} copied to ${locationsCountPhrase}, ${productsNotCopiedCountPhrase} unavailable products skipped`;
      toast.success({
        position: 'topRight',
        message,
      });
    } catch (e) {
      logger.error(e);
      toast.error({
        position: 'topRight',
        message: `An error occurred while copying products`,
      });
    }
  };

  const productCountPhrase = buildCountPhrase(productNames.length, '', 'product', 'products');
  return (
    <Box
      sx={{
        borderRadius: '8px',
        border: `1px solid ${theme.palette.SlabGray.Stroke}`,
        background: 'white',
        width: '25rem',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', margin: '0.75rem', gap: '0.5rem' }}>
        <Typography variant='h5/medium'> Copy products to another location(s) </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', color: '#727382' }}>
          <Box1 variant='TwoTone' style={{ marginRight: '0.25rem' }} />
          {productCountPhrase} from {plantName}
        </Box>
        <InputSearch
          value={search}
          onChange={setSearch}
          sx={{
            padding: '0',
            '.MuiInputBase-input': {
              padding: '0.5rem 0.5rem 0.5rem 2.5rem',
            },
          }}
        />
      </Box>
      <Box
        sx={{
          width: '100%',
          height: '17rem',
          overflow: 'auto',
          padding: '0 0.75rem',
          borderTop: `1px solid ${theme.palette.SlabGray.Stroke}`,
          borderBottom: `1px solid ${theme.palette.SlabGray.Stroke}`,
        }}
      >
        <ProductLocationSelectList
          isLoading={isLoading}
          plants={filteredPlants}
          selectedPlants={selectedPlantsIds}
          onPlantSelectionChange={setSelectedPlantsIds}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', padding: '0.75rem', gap: '1rem' }}>
        <Button variant='slab-secondary' size='small' onClick={onClose} sx={{ flex: 1 }}>
          Cancel
        </Button>
        <Button
          disabled={isLoading || selectedPlantsIds.length === 0}
          variant='slab-primary'
          size='small'
          sx={{ flex: 1 }}
          onClick={onCopyClick}
        >
          Copy Products
        </Button>
      </Box>
    </Box>
  );
};
